import * as Yup from 'yup';

import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { OrgManager, patchUser, patchUserProfile, UserPrefix, UserSuffix } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { useAvailableSuffixesQuery, useUserProfileQuery } from '@uplift/queries';
import {
  AutocompleteMulti, getRenderTagsForAutocompleteMulti, Select, SelectItem, TextInput
} from '@uplift/ui-v2';
import { VALIDATION_FIRST_NAME, VALIDATION_LAST_NAME } from '@uplift/validations';

import { useEditManagerProfileHandlers } from '../hooks';

export type ManagerNameFormType = {
  id: string;
  prefix: UserPrefix | null;
  suffixes: UserSuffix[] | null;
  firstName: string | null;
  lastName: string | null;
};

export const useManagerNameForm = (managerData: OrgManager) => {
  const { id: managerId, firstName, lastName } = managerData.user;
  const { data: profile } = useUserProfileQuery(managerId);

  const { onFormSubmitSuccess, onFormSubmitError } = useEditManagerProfileHandlers(managerId, 'Name');

  const managerNameFormik = () => {
    const initialValues: ManagerNameFormType = {
      id: managerId,
      prefix: profile?.prefix ?? null,
      suffixes: profile?.suffixes ?? [],
      firstName: firstName,
      lastName: lastName,
    };

    const validationSchema = Yup.object({
      prefix: Yup.string().nullable(),
      suffixes: Yup.array(Yup.string()).nullable(),
      firstName: VALIDATION_FIRST_NAME.required('Enter manager’s first name'),
      lastName: VALIDATION_LAST_NAME.required('Enter manager’s last name'),
    });

    const onSubmit = async (values: ManagerNameFormType) => {
      try {
        await Promise.all([
          patchUser(managerId, { firstName: values.firstName ?? '', lastName: values.lastName ?? '' }),
          patchUserProfile(managerId, {
            prefix: values.prefix,
            suffixes: values.suffixes,
          }),
        ]);
        await onFormSubmitSuccess(true);
      } catch (error) {
        onFormSubmitError();
      }
    };

    return { initialValues, validationSchema, onSubmit };
  };

  return { managerNameFormik };
};

export const ManagerNameForm: React.VFC = () => {
  const { classes } = useManagerNameFormStyles();
  const l10n = useL10n();
  const { data: suffixes } = useAvailableSuffixesQuery();

  const suffixOptions = suffixes?.map(value => ({
    id: value,
    label: value
  }));

  return (
    <div className={classes.formGroup}>
      <Select
        label={l10n.accountSettings.isOptional(l10n.providerSettings.prefix)}
        name="prefix"
        className={classes.input}
        data-testid="prefix-select"
      >
        <SelectItem value={UserPrefix.dr}>
          {UserPrefix.dr}
        </SelectItem>
        <SelectItem value={null}>
          <Typography component="i" color="textSecondary">{l10n.general.none}</Typography>
        </SelectItem>
      </Select>
      <TextInput
        label={l10n.accountSettings.firstName}
        name="firstName"
        fullWidth
        className={classes.input}
        data-testid="first-name-input"
      />
      <TextInput
        label={l10n.accountSettings.lastName}
        name="lastName"
        fullWidth
        className={classes.input}
        data-testid="last-name-input"
      />
      <AutocompleteMulti
        label={l10n.accountSettings.isOptional(l10n.providerSettings.suffix)}
        name="suffixes"
        options={suffixOptions ?? []}
        extractId
        getOptionLabel={opt => opt?.label}
        renderTags={getRenderTagsForAutocompleteMulti(opt => opt?.label)}
        className={classes.input}
        data-testid="suffixes-multi-select"
      />
    </div>
  );
};

export const useManagerNameFormStyles = makeStyles({ name: 'ManagerNameForm' })((theme) => ({
  input: {
    '& label.Mui-focused': {
      color: theme.palette.secondary.main,
    },
    '& .Mui-checked': {
      color: theme.palette.secondary.main,
    },
    '& .Mui-focused:not(.Mui-error) fieldset': {
      borderColor: theme.palette.secondary.main,
    },
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    maxWidth: '400px'
  },
}));
